import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  defaultSegmentsFromPlain,
  newChipId,
  normalizeSegments,
  segmentsToPlainText,
  uniqueTagNamesInOrder,
} from './utils/transcriptInlineChipUtils';
import { Dialog, useMediaQuery } from '@mui/material';
import { qcAxios } from '../../api/axiosQcClient';
import {
  buildQcAssessmentDefaultValues,
  buildQcAssessmentFormSchema,
} from '../../schemas';
import { useAppSelector } from '../../store/hooks';
import QcAssessmentDesktopLayout from './QcAssessmentDesktopLayout';
import QcAssessmentMobileLayout from './QcAssessmentMobileLayout';
import {
  buildQuestionsPayload,
  buildClassificationTagNamesForWorkflow,
  computeTranscriptChangePercentage,
  mergeAllWorkflowTagRowsUnique,
  getLinkedTranscriptQuestionIds,
  getQuestionAnswer,
  getFinalSubmitReqStatus,
  getResponseToReqEffectiveBase,
  getResponseToReqSubmissionMeta,
  hasAtLeastOneDraftProgress,
  isAssessmentReadOnly,
  mergeClassificationTagsIntoResponse,
  mergeTranscriptAndLinkedQuestions,
  parseClassificationTagsFromResponse,
  sortQuestions,
  stripHtmlTags,
} from './utils/qcAssessmentQuestionUtils';

function truncateMiddle(str, maxLen = 24) {
  if (!str || str.length <= maxLen) return str;
  const half = Math.floor((maxLen - 3) / 2);
  return `${str.slice(0, half)}...${str.slice(-half)}`;
}

/** Same viewport as MUI default `theme.breakpoints.down('md')` (900px). */
const MOBILE_MEDIA_QUERY = '(max-width:899.95px)';
const TRANSCRIPT_WORKFLOW_KEY = 'QC-WF-TRANSCRIPT-01';

const SUBMIT_ASSIGNED_REQUESTS_ENDPOINT =
  '/api/assignmentData/submitAssignedRequests';

export default function QcAssessmentModal({
  open,
  onClose,
  task,
  /** When assignment rows omit `workflow_id`, use the workflow selected on the QC page (URL filter). */
  pageWorkflowId,
  onMoveToNextTask,
  /** Reports / audit views: never allow edit or submit regardless of `task` shape. */
  forceReadOnly = false,
  reviewer = false,
  onApprove,
  onApproveNext,
  onReject,
  isPendingReview = false,
}) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, { noSsr: true });

  /** Match API `getAllTags` / Redux keys (e.g. QC-WF-TRANSCRIPT-01). */
  const resolvedWorkflowIdForTags = useMemo(() => {
    const raw = task?.workflow_id ?? task?.workflowId ?? pageWorkflowId ?? '';
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    return s !== '' ? s : '';
  }, [task?.workflow_id, task?.workflowId, pageWorkflowId]);

  const tagsByWorkflowIdFromStore = useAppSelector(
    (state) => state.workflowTags.tagsByWorkflowId,
  );
  const globalWorkflows = useAppSelector(
    (state) => state.workflowTags.globalWorkflows,
  );

  /** When workflow tags finish loading, re-hydrate so saved ids/names map to API tag strings. */
  const workflowTagsSyncKey = useAppSelector(
    (state) =>
      `${String(state.workflowTags.updatedAt ?? '')}|${String(state.workflowTags.version ?? '')}`,
  );

  /** Tag rows from Redux for this workflow (re-renders when bootstrap finishes). */
  const workflowTagRows = useMemo(() => {
    if (resolvedWorkflowIdForTags) {
      const list = tagsByWorkflowIdFromStore[resolvedWorkflowIdForTags];
      return Array.isArray(list) ? list : [];
    }
    return mergeAllWorkflowTagRowsUnique(tagsByWorkflowIdFromStore);
  }, [resolvedWorkflowIdForTags, tagsByWorkflowIdFromStore]);

  /** Tag names only (unique, sorted) for the dropdown. */
  const classificationTagOptions = useMemo(
    () => buildClassificationTagNamesForWorkflow(workflowTagRows),
    [workflowTagRows],
  );

  const sortedQuestions = useMemo(
    () => sortQuestions(task?.questions ?? task?.response_to_req?.questions),
    [task?.questions, task?.response_to_req?.questions],
  );

  const linkedTranscriptQuestionIds = useMemo(
    () => getLinkedTranscriptQuestionIds(sortedQuestions),
    [sortedQuestions],
  );

  const linkedTranscriptQuestionIdSet = useMemo(
    () => new Set(linkedTranscriptQuestionIds.map(String)),
    [linkedTranscriptQuestionIds],
  );

  const transcriptWorkflowId = useMemo(() => {
    if (!Array.isArray(globalWorkflows)) return '';
    const row = globalWorkflows.find((w) => {
      const workflowId = w?.workflow_id ?? w?.workflowId;
      return String(workflowId ?? '').trim() === TRANSCRIPT_WORKFLOW_KEY;
    });
    return String(row?.workflow_id ?? row?.workflowId ?? '').trim();
  }, [globalWorkflows]);

  const showTranscriptFields = useMemo(() => {
    const wf = task?.workflow_id ?? task?.workflowId ?? pageWorkflowId ?? '';
    return String(wf).trim() === transcriptWorkflowId;
  }, [
    task?.workflow_id,
    task?.workflowId,
    pageWorkflowId,
    transcriptWorkflowId,
  ]);

  const readOnly = useMemo(
    () => !reviewer && (Boolean(forceReadOnly) || isAssessmentReadOnly(task)),
    [forceReadOnly, task, reviewer],
  );

  const qcFormSchema = useMemo(
    () => buildQcAssessmentFormSchema(sortedQuestions),
    [sortedQuestions],
  );

  const emptyAssessmentDefaults = useMemo(
    () => ({ answers: {}, updatedTranscript: '' }),
    [],
  );

  const formMethods = useForm({
    resolver: readOnly ? undefined : zodResolver(qcFormSchema),
    defaultValues: emptyAssessmentDefaults,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { reset, handleSubmit, setValue, getValues } = formMethods;

  /** Inline transcript: text + tag chips; API transcript keeps `[TagName]` markers. */
  const [transcriptSegments, setTranscriptSegments] = useState(() =>
    defaultSegmentsFromPlain(''),
  );
  const [transcriptChangePercentage, setTranscriptChangePercentage] =
    useState(0);
  const [transcriptDiffExpanded, setTranscriptDiffExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const linkedTranscriptCommitTimerRef = useRef(null);

  const classificationTags = useMemo(
    () => uniqueTagNamesInOrder(transcriptSegments),
    [transcriptSegments],
  );

  /** Ref to `TranscriptInlineChipEditor` (`insertTag`, `insertPlainTextAtCaret`, …). */
  const transcriptInputRef = useRef(null);

  const vendorPlainForChangeStats = useMemo(() => {
    const vendor = task?.metadata?.Vendor_Trans ?? task?.metadata?.vendor_trans;
    return stripHtmlTags(vendor ?? '');
  }, [task?.metadata?.Vendor_Trans, task?.metadata?.vendor_trans]);

  /** Context / parent often pass new array refs each render; do not put those in effect deps (reset loop). */
  const workflowTagRowsRef = useRef(workflowTagRows);
  workflowTagRowsRef.current = workflowTagRows;
  const sortedQuestionsRef = useRef(sortedQuestions);
  sortedQuestionsRef.current = sortedQuestions;

  const assessmentHydrationKey = useMemo(() => {
    const id = task?.assignment_id ?? task?.assignmentId ?? '';
    const raw = task?.response_to_req;
    let rawKey = '';
    if (raw != null && raw !== '') {
      if (typeof raw === 'string') {
        rawKey = raw;
      } else {
        try {
          rawKey = JSON.stringify(raw);
        } catch {
          // Cyclic or un-serializable payload — fall back to a stable but less
          // precise key so React Hook Form still hydrates and we don't crash.
          rawKey = `non-serializable:${typeof raw}`;
        }
      }
    }
    const qIds = sortedQuestions
      .map((q, i) => String(q?.question_id ?? i))
      .join(',');
    const vendorRaw = String(
      task?.metadata?.Vendor_Trans ?? task?.metadata?.vendor_trans ?? '',
    );
    const vendorFp = `${vendorRaw.length}|${vendorRaw.slice(0, 96)}`;
    return `${open ? 1 : 0}|${id}|${rawKey}|${qIds}|${workflowTagsSyncKey}|${vendorFp}`;
  }, [
    open,
    task?.assignment_id,
    task?.assignmentId,
    task?.response_to_req,
    task?.metadata?.Vendor_Trans,
    task?.metadata?.vendor_trans,
    sortedQuestions,
    workflowTagsSyncKey,
  ]);

  useEffect(() => {
    if (!open) return;
    const sq = sortedQuestionsRef.current;
    const tagRows = workflowTagRowsRef.current;

    const raw = task?.response_to_req;
    if (raw == null || raw === '') {
      reset(buildQcAssessmentDefaultValues(sq, null, ''));
      setTranscriptSegments(defaultSegmentsFromPlain(''));
      setTranscriptChangePercentage(0);
      setTranscriptDiffExpanded(false);
      return;
    }
    let data = raw;
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch {
        reset(buildQcAssessmentDefaultValues(sq, null, ''));
        setTranscriptSegments(defaultSegmentsFromPlain(''));
        setTranscriptChangePercentage(0);
        setTranscriptDiffExpanded(false);
        return;
      }
    }
    if (data == null) {
      reset(buildQcAssessmentDefaultValues(sq, null, ''));
      setTranscriptSegments(defaultSegmentsFromPlain(''));
      setTranscriptChangePercentage(0);
      setTranscriptDiffExpanded(false);
      return;
    }
    if (typeof data === 'object') {
      if (Array.isArray(data)) {
        reset(buildQcAssessmentDefaultValues(sq, data, ''));
        setTranscriptSegments(defaultSegmentsFromPlain(''));
        setTranscriptChangePercentage(0);
        setTranscriptDiffExpanded(false);
      } else {
        const tags = parseClassificationTagsFromResponse(data, tagRows);
        const linkedIds = getLinkedTranscriptQuestionIds(sq);
        // Stored payload writes `updated_vendor_transcript` (see buildApiPayload
        // below); `updatedTranscript` is only the React Hook Form field name and
        // never appears in API/DB data — check it too only as a legacy fallback.
        let ut =
          typeof data.updated_vendor_transcript === 'string'
            ? data.updated_vendor_transcript
            : typeof data.updatedTranscript === 'string'
              ? data.updatedTranscript
              : '';
        if (!ut && linkedIds.length > 0) {
          for (const id of linkedIds) {
            const a = getQuestionAnswer(data, id);
            if (a != null && String(a) !== '') {
              ut = String(a);
              break;
            }
          }
        }
        const tagSegs = tags
          .map((name) => ({
            type: 'tag',
            name: String(name).trim(),
            id: newChipId(),
          }))
          .filter((s) => s.name);
        setTranscriptSegments(
          normalizeSegments([...tagSegs, { type: 'text', text: ut }]),
        );
        const merged = mergeTranscriptAndLinkedQuestions(
          mergeClassificationTagsIntoResponse({ ...data }, tags),
          ut,
          linkedIds,
        );
        reset(buildQcAssessmentDefaultValues(sq, merged, ut));
        setTranscriptDiffExpanded(false);
        const base = getResponseToReqEffectiveBase(data) ?? data;
        const meta = getResponseToReqSubmissionMeta(base);
        const vendorStr =
          task?.metadata?.Vendor_Trans ?? task?.metadata?.vendor_trans ?? '';
        const vendorPlain = stripHtmlTags(vendorStr);
        let initialPct = 0;
        if (
          meta.hasChangePercentageKey &&
          meta.changePercentage != null &&
          Number.isFinite(Number(meta.changePercentage))
        ) {
          initialPct = Number(meta.changePercentage);
        } else {
          initialPct = computeTranscriptChangePercentage(vendorPlain, ut);
        }
        setTranscriptChangePercentage(initialPct);
      }
    } else {
      reset(buildQcAssessmentDefaultValues(sq, null, ''));
      setTranscriptSegments(defaultSegmentsFromPlain(''));
      setTranscriptChangePercentage(0);
      setTranscriptDiffExpanded(false);
    }
  }, [assessmentHydrationKey, reset]);

  useEffect(() => {
    return () => {
      if (linkedTranscriptCommitTimerRef.current != null) {
        clearTimeout(linkedTranscriptCommitTimerRef.current);
        linkedTranscriptCommitTimerRef.current = null;
      }
    };
  }, []);

  const handleTranscriptSegmentsChange = useCallback(
    (next) => {
      if (readOnly) return;
      const norm = normalizeSegments(next);
      setTranscriptSegments(norm);
      const plainForUpdatedTranscript = segmentsToPlainText(norm);
      const plainForLinkedTranscript = segmentsToPlainText(norm, {
        wrapTagsInBrackets: false,
      });
      setValue('updatedTranscript', plainForUpdatedTranscript, {
        shouldValidate: false,
        shouldDirty: true,
      });
      for (const id of linkedTranscriptQuestionIds) {
        setValue(`answers.${String(id)}`, plainForLinkedTranscript, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    },
    [readOnly, linkedTranscriptQuestionIds, setValue],
  );

  const commitTranscriptChangePercentage = useCallback(() => {
    if (!showTranscriptFields) return;
    const ut = getValues('updatedTranscript');
    const pct = computeTranscriptChangePercentage(
      vendorPlainForChangeStats,
      String(ut ?? ''),
    );
    setTranscriptChangePercentage(pct);
  }, [getValues, showTranscriptFields, vendorPlainForChangeStats]);

  const handlePlainTranscriptFromLinkedField = useCallback(
    (plain) => {
      if (readOnly) return;
      setTranscriptSegments(defaultSegmentsFromPlain(plain));
      if (!showTranscriptFields) return;
      if (linkedTranscriptCommitTimerRef.current != null) {
        clearTimeout(linkedTranscriptCommitTimerRef.current);
      }
      linkedTranscriptCommitTimerRef.current = setTimeout(() => {
        linkedTranscriptCommitTimerRef.current = null;
        const ut = String(plain ?? '');
        const pct = computeTranscriptChangePercentage(
          vendorPlainForChangeStats,
          ut,
        );
        setTranscriptChangePercentage(pct);
      }, 400);
    },
    [readOnly, showTranscriptFields, vendorPlainForChangeStats],
  );

  const handleLinkedTranscriptFieldCommitted = useCallback(() => {
    if (linkedTranscriptCommitTimerRef.current != null) {
      clearTimeout(linkedTranscriptCommitTimerRef.current);
      linkedTranscriptCommitTimerRef.current = null;
    }
    commitTranscriptChangePercentage();
  }, [commitTranscriptChangePercentage]);

  /** Insert tag chip at caret inside the transcript editor. */
  const handleAddClassificationTag = useCallback(
    (tagName) => {
      const t = tagName != null ? String(tagName).trim() : '';
      if (!t || readOnly) return;
      transcriptInputRef.current?.insertTag(t);
    },
    [readOnly],
  );

  const taskMeta = useMemo(() => {
    const fileName = task?.file_name ?? '—';
    const vendor = task?.metadata?.Vendor_Trans ?? task?.metadata?.vendor_trans;
    const originalTranscript =
      vendor || 'No vendor transcript provided.';
    return {
      requestId: task?.req_id ?? '—',
      fileName,
      fileNameShort: truncateMiddle(String(fileName), 22),
      state: task?.state ?? '—',
      district: task?.district ?? '—',
      language: task?.language ?? '—',
      originalTranscript,
      /** Same as `task.file_path` — used for audio controls and spectrogram iframe. */
      filePath: task?.file_path ?? '',
      audioUrl: task?.file_path ?? '',
      durationSec: task?.duration,
      sourceFile: task?.source_file,
      assignedTo: task?.assigned_to?.name ?? task?.assigned_to?.email ?? null,
    };
  }, [task]);

  const buildApiPayload = useCallback(
    (formSnapshot) => {
      const data = formSnapshot ?? getValues();
      const ut =
        typeof data?.updatedTranscript === 'string'
          ? data.updatedTranscript
          : '';
      const linkedTranscriptText = segmentsToPlainText(transcriptSegments, {
        wrapTagsInBrackets: false,
      });
      const answersPayload = mergeClassificationTagsIntoResponse(
        { ...(data?.answers ?? {}) },
        classificationTags,
      );
      const answersForQuestions = mergeTranscriptAndLinkedQuestions(
        answersPayload,
        ut,
        linkedTranscriptQuestionIds,
        linkedTranscriptText,
      );
      // `task?.questions` is null for any already-submitted assignment (the
      // backend only attaches it for brand-new OPEN rows) — use the
      // fallback-aware `sortedQuestions` so re-saving doesn't wipe out the
      // question definitions stored in response_to_req.
      const questionsPayload = buildQuestionsPayload(
        sortedQuestions,
        answersForQuestions,
      );
      const assignmentId = String(
        task?.assignment_id ?? task?.assignmentId ?? '',
      );
      const payload = {
        response: {
          assignment_id: assignmentId,
          questions: questionsPayload,
          updated_vendor_transcript: showTranscriptFields
            ? String(ut ?? '')
            : '',
          change_percentage: showTranscriptFields
            ? transcriptChangePercentage
            : 0,
        },
      };
      return payload;
    },
    [
      sortedQuestions,
      task?.assignment_id,
      task?.assignmentId,
      getValues,
      linkedTranscriptQuestionIds,
      showTranscriptFields,
      classificationTags,
      transcriptSegments,
      transcriptChangePercentage,
    ],
  );

  const handleSaveAndNext = useCallback(() => {
    if (!task || isSaving || isSubmitting) return;

    const runSave = async (snapshot) => {
      setIsSaving(true);
      try {
        const assignmentId =
          task?.assignment_id ?? task?.assignmentId ?? task?.id ?? '';
        const payload = buildApiPayload(snapshot);
        await qcAxios.post(SUBMIT_ASSIGNED_REQUESTS_ENDPOINT, payload, {
          params: {
            assignmentId,
            reqStatus: 'IN-PROGRESS',
          },
        });
        if (typeof onMoveToNextTask === 'function') {
          onMoveToNextTask(task);
        } else {
          onClose?.();
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          'Failed to save assessment.';
        // eslint-disable-next-line no-alert
        globalThis.alert(msg);
      } finally {
        setIsSaving(false);
      }
    };

    if (readOnly) {
      void runSave(getValues());
      return;
    }

    const snapshot = getValues();
    if (
      !hasAtLeastOneDraftProgress(snapshot, {
        sortedQuestions,
        classificationTags,
        showTranscriptFields,
      })
    ) {
      // eslint-disable-next-line no-alert
      globalThis.alert(
        'Enter at least one answer, updated transcript text, or classification tag to save your progress.',
      );
      return;
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[QcAssessment] save draft (partial)', snapshot);
    }
    void runSave(snapshot);
  }, [
    task,
    isSaving,
    isSubmitting,
    readOnly,
    buildApiPayload,
    getValues,
    sortedQuestions,
    classificationTags,
    showTranscriptFields,
    onMoveToNextTask,
    onClose,
  ]);

  const submitAssessment = useCallback(
    async (snapshot, onSuccess) => {
      setIsSubmitting(true);
      try {
        const assignmentId =
          task?.assignment_id ?? task?.assignmentId ?? task?.id ?? '';
        const payload = buildApiPayload(snapshot);
        await qcAxios.post(SUBMIT_ASSIGNED_REQUESTS_ENDPOINT, payload, {
          params: {
            assignmentId,
            reqStatus: getFinalSubmitReqStatus(task),
          },
        });
        onSuccess?.();
      } catch (err) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          'Failed to submit assessment.';
        // eslint-disable-next-line no-alert
        globalThis.alert(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [task, buildApiPayload],
  );

  const runValidatedSubmit = useCallback(
    (onSuccess) => {
      if (!task || isSubmitting || isSaving) return;

      const runSubmit = async (snapshot) => {
        await submitAssessment(snapshot, onSuccess);
      };

      if (readOnly) {
        void runSubmit(getValues());
        return;
      }

      handleSubmit(
        async (data) => {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log('[QcAssessment] valid assessment form data', data);
          }
          await runSubmit(data);
        },
        (errors) => {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('[QcAssessment] form validation failed', errors);
          }
        },
      )();
    },
    [
      task,
      isSubmitting,
      isSaving,
      readOnly,
      getValues,
      handleSubmit,
      submitAssessment,
    ],
  );

  const handleSubmitAndClose = useCallback(() => {
    runValidatedSubmit(() => {
      onClose?.();
    });
  }, [runValidatedSubmit, onClose]);

  const handleSubmitAndNext = useCallback(() => {
    runValidatedSubmit(() => {
      if (typeof onMoveToNextTask === 'function') {
        onMoveToNextTask(task);
      } else {
        onClose?.();
      }
    });
  }, [task, runValidatedSubmit, onMoveToNextTask, onClose]);

  const buildReviewPayload = useCallback(() => {
    const ut = segmentsToPlainText(transcriptSegments, { wrapTagsInBrackets: false });
    const answers = getValues().answers ?? {};
    const questions = buildQuestionsPayload(sortedQuestions, answers);
    return { ut, questions };
  }, [transcriptSegments, sortedQuestions, getValues]);

  const handleReviewApprove = useCallback(() => {
    const { ut, questions } = buildReviewPayload();
    onApprove?.(null, ut, null, questions);
  }, [buildReviewPayload, onApprove]);

  const handleReviewApproveNext = useCallback(() => {
    const { ut, questions } = buildReviewPayload();
    onApproveNext?.(null, ut, null, questions);
  }, [buildReviewPayload, onApproveNext]);

  const handleReviewReject = useCallback(() => {
    const { ut, questions } = buildReviewPayload();
    onReject?.(null, ut, null, questions);
  }, [buildReviewPayload, onReject]);

  const handleReviewSkip = useCallback(() => {
    if (typeof onMoveToNextTask === 'function') {
      onMoveToNextTask(task);
    } else {
      onClose?.();
    }
  }, [task, onMoveToNextTask, onClose]);

  const handleTranscriptDiffExpandedChange = useCallback((expanded) => {
    setTranscriptDiffExpanded(Boolean(expanded));
  }, []);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      scroll="paper"
      // Lets OS IME / browser extensions (e.g. Google Input Tools) keep focus while typing.
      // Without this, the modal focus trap can interrupt transliteration in form fields.
      disableEnforceFocus
      slotProps={{
        // App shell uses `z-99999` (see AppHeader). MUI modal default (~1300) sits under it.
        root: {
          sx: (theme) => ({
            zIndex: theme.zIndex.modal + 100_000,
          }),
        },
        backdrop: {
          sx: (theme) => ({
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'rgba(0, 0, 0, 0.72)'
                : 'rgba(13, 28, 46, 0.35)',
          }),
        },
        paper: {
          sx: (theme) => ({
            m: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            minHeight: '100dvh',
            maxHeight: '100dvh',
            borderRadius: 0,
            overflow: 'hidden',
            bgcolor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : '#f8f9ff',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <FormProvider {...formMethods}>
        {isMobile ? (
          <QcAssessmentMobileLayout
            onClose={onClose}
            taskMeta={taskMeta}
            sortedQuestions={sortedQuestions}
            readOnly={readOnly}
            linkedTranscriptQuestionIdSet={linkedTranscriptQuestionIdSet}
            linkedTranscriptQuestionIds={linkedTranscriptQuestionIds}
            onPlainTranscriptFromLinkedField={
              handlePlainTranscriptFromLinkedField
            }
            transcriptSegments={transcriptSegments}
            onTranscriptSegmentsChange={handleTranscriptSegmentsChange}
            transcriptInputRef={transcriptInputRef}
            classificationTags={classificationTags}
            onAddClassificationTag={handleAddClassificationTag}
            classificationTagOptions={classificationTagOptions}
            showTranscriptFields={showTranscriptFields}
            vendorPlainForDiff={vendorPlainForChangeStats}
            transcriptChangePercentage={transcriptChangePercentage}
            transcriptDiffExpanded={transcriptDiffExpanded}
            onTranscriptDiffExpandedChange={handleTranscriptDiffExpandedChange}
            onTranscriptEditorCommit={commitTranscriptChangePercentage}
            onLinkedTranscriptFieldCommitted={
              handleLinkedTranscriptFieldCommitted
            }
            onSaveAndNext={handleSaveAndNext}
            onSubmitAndNext={handleSubmitAndNext}
            onSubmitAndClose={handleSubmitAndClose}
            isSaving={isSaving}
            isSubmitting={isSubmitting}
            reviewer={reviewer}
            onReviewApprove={handleReviewApprove}
            onReviewApproveNext={handleReviewApproveNext}
            onReviewReject={handleReviewReject}
            onReviewSkip={handleReviewSkip}
            isPendingReview={isPendingReview}
          />
        ) : (
          <QcAssessmentDesktopLayout
            onClose={onClose}
            taskMeta={taskMeta}
            sortedQuestions={sortedQuestions}
            readOnly={readOnly}
            linkedTranscriptQuestionIdSet={linkedTranscriptQuestionIdSet}
            linkedTranscriptQuestionIds={linkedTranscriptQuestionIds}
            onPlainTranscriptFromLinkedField={
              handlePlainTranscriptFromLinkedField
            }
            transcriptSegments={transcriptSegments}
            onTranscriptSegmentsChange={handleTranscriptSegmentsChange}
            transcriptInputRef={transcriptInputRef}
            classificationTags={classificationTags}
            onAddClassificationTag={handleAddClassificationTag}
            classificationTagOptions={classificationTagOptions}
            showTranscriptFields={showTranscriptFields}
            vendorPlainForDiff={vendorPlainForChangeStats}
            transcriptChangePercentage={transcriptChangePercentage}
            transcriptDiffExpanded={transcriptDiffExpanded}
            onTranscriptDiffExpandedChange={handleTranscriptDiffExpandedChange}
            onTranscriptEditorCommit={commitTranscriptChangePercentage}
            onLinkedTranscriptFieldCommitted={
              handleLinkedTranscriptFieldCommitted
            }
            onSaveAndNext={handleSaveAndNext}
            onSubmitAndNext={handleSubmitAndNext}
            onSubmitAndClose={handleSubmitAndClose}
            isSaving={isSaving}
            isSubmitting={isSubmitting}
            reviewer={reviewer}
            onReviewApprove={handleReviewApprove}
            onReviewApproveNext={handleReviewApproveNext}
            onReviewReject={handleReviewReject}
            onReviewSkip={handleReviewSkip}
            isPendingReview={isPendingReview}
          />
        )}
      </FormProvider>
    </Dialog>
  );
}
